import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnInit
} from '@angular/core';
import { ReactiveFormsModule, FormsModule, FormControl, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import mockupsData from '../../assets/mockups.json';
import { Mockup, Zoom } from '../shared/interfaces/mockup.interface';
import { ConvertService } from '../shared/services/convert.service';
import { SimulationService } from '../shared/services/simulation.service';
import { UserService } from '../shared/services/user.service';
import { CanvasComponent } from "../canvas/canvas.component";
import { ProcessResponse } from "../shared/interfaces/process-response.interface";
import { NgxScrollAnimationsDirective } from 'ngx-scroll-animations';
import { Platform } from '@angular/cdk/platform';
import { PdfViewer } from "../pdf-viewer/pdf-viewer";
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';

enum ErrorMessage {
  NONE = 0,
  UNSUPPORTED_FILE_TYPE = 1,
  NO_MATCHING_FORMAT = 2,
  TOO_MANY_PAGES = 3,
}

interface PageResult {
  file: ProcessResponse | null;
  callasFiles: {
    bat: string | null;
    prod: string | null;
    prodIsZip: boolean;
    errors: string[] | null;
    checks: string[] | null;
    corrections: string[] | null
  } | null;
  callasRunning: boolean;
  previewPdf: string | null;
  skipped: boolean; // true when page dimensions don't match page 0
}

@Component({
  selector: 'app-home',
  standalone: true,
  templateUrl: './home.component.html',
  styleUrls: ['./home.animation.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CanvasComponent,
    NgxScrollAnimationsDirective,
    PdfViewer,
    HeaderComponent,
    FooterComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeComponent implements AfterViewInit, OnInit {
  @ViewChild('gradientElement') gradientElement!: ElementRef<HTMLElement>;

  protected readonly window = window;
  private static readonly RATIO_EPS = 0.05;
  private abortController = new AbortController();
  private activeIntervals: ReturnType<typeof setInterval>[] = [];
  convertService = inject(ConvertService);
  protected platform = inject(Platform);

  visualFile = signal<ProcessResponse | null>(null);
  isLoading = signal<boolean>(false);
  isDragOver = signal<boolean>(false);
  mockups = signal<Mockup[]>(mockupsData as Mockup[]);
  selectedMockupIdx = signal<number>(1);
  isNight = signal<boolean>(true);
  selectedZoomIdx = signal<number>(2);
  errorMsg = signal<number>(ErrorMessage.NONE);
  pickerVisible = signal<boolean>(false);
  pickerMatches = signal<number[]>([]);
  isFullscreen = signal<boolean>(false);
  state = signal<number>(0);

  // Multi-page
  pages = signal<PageResult[]>([]);
  selectedPageIdx = signal<number>(0);
  totalPages = signal<number>(0);

  // Derived from the currently selected page — same names as before so the template is unchanged
  file = computed(() => this.pages()[this.selectedPageIdx()]?.file ?? null);
  callasFiles = computed(() => this.pages()[this.selectedPageIdx()]?.callasFiles ?? null);
  callasRunning = computed(() => this.pages()[this.selectedPageIdx()]?.callasRunning ?? false);
  previewPdf = computed(() => this.pages()[this.selectedPageIdx()]?.previewPdf ?? null);

  visiblePageCount = computed(() => this.pages().filter(p => !p.skipped).length);
  pageDropdownOpen = signal(false);
  pageDropdownOpenRight = signal(false);
  errorPageCount = computed(() => this.pages().filter(p => (p.callasFiles?.errors?.length ?? 0) > 0).length);
  invalidPageCount = computed(() => this.pages().filter(p => p.skipped).length);
  errorSummaryText = computed(() => {
    const errors = this.errorPageCount();
    const invalid = this.invalidPageCount();
    const parts: string[] = [];
    if (errors > 0) parts.push(`${errors} page${errors > 1 ? 's' : ''} en erreur`);
    if (invalid > 0) parts.push(`${invalid} page${invalid > 1 ? 's' : ''} invalide${invalid > 1 ? 's' : ''}`);
    return parts.join(' et ');
  });

  anyRunning = computed(() => this.isLoading() || this.pickerVisible() || this.pages().some(p => p.callasRunning));
  allDone = computed(() =>
    !this.anyRunning() &&
    this.pages().length > 0 &&
    this.pages().every(p => p.skipped || p.file !== null)
  );
  canDownload = computed(() =>
    !this.isLoading() &&
    !this.pickerVisible() &&
    this.pages().some(p => !p.skipped && p.callasFiles?.prod != null)
  );
  canDownloadCurrentPage = computed(() => this.callasFiles()?.prod != null);
  allDownloadable = computed(() =>
    !this.pages().some(p => p.callasRunning) &&
    this.pages().filter(p => !p.skipped).length > 1 &&
    this.pages().every(p => p.skipped || p.callasFiles !== null) &&
    this.pages().some(p => !p.skipped && p.callasFiles?.prod != null)
  );
  downloadPopupOpen = signal(false);
  orderNumber = new FormControl('', { nonNullable: true, validators: [Validators.required] });
  downloadScope = signal<'current' | 'all'>('current');
  loadingMessage = computed(() => {
    if (this.isLoading() || this.pickerVisible()) {
      return this.totalPages() > 1 ? 'Traitement du document en cours...' : 'Traitement en cours...';
    }
    const runningIdx = this.pages().findIndex(p => p.callasRunning);
    if (runningIdx >= 0) {
      const total = this.totalPages();
      return total > 1
        ? `Contrôle qualité — page ${runningIdx + 1} / ${total}`
        : 'Contrôle qualité en cours...';
    }
    if (this.allDone() && this.pages().length > 0) return 'Fichiers prêts à l\'impression';
    return '';
  });

  private simulationService = inject(SimulationService);
  private userService = inject(UserService);

  private pageFiles = signal<File[]>([]);
  private pendingFile = signal<ProcessResponse | null>(null);
  private fileTmp = signal<File | null>(null);
  private refDimensions = signal<{ width: number; height: number } | null>(null);
  private originalFileName = signal('');
  private currentFileType = signal<string | null>(null);
  private simulationSaved = signal(false);

  constructor() {
    this.pickerMatches.set([]);
    this.pickerVisible.set(false);

    // Auto-reset when the selected page has no callasFiles (e.g. after switching pages)
    effect(() => {
      if (this.state() !== 0 && !this.callasFiles()) {
        this.state.set(0);
      }
    });

    window.addEventListener('fullscreenchange', () => {
      if (document.fullscreenElement) {
        this.isFullscreen.set(true);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        window.screen.orientation?.lock('landscape-primary');
      } else {
        this.isFullscreen.set(false);
        window.screen.orientation?.unlock();
      }
    });
  }

  async ngOnInit(): Promise<void> {
    await this.loadRandomVisuals();
    this.userService.saveUser();
  }

  private async loadRandomVisuals(): Promise<void> {
    const visualFiles = [
      '2m2-Bleu.png',
      '2m2-Jaune.png',
      '2m2-Marine.png',
      '2m2-Orange.png',
      '2m2-Rose.png',
      '2m2-Rouge.png',
      '2m2-Vert.png',
      '2m2-Violet.png',
    ];
    try {
      const randomFile = visualFiles[Math.floor(Math.random() * visualFiles.length)];
      const url = `assets/visuals/${randomFile}`;
      this.visualFile.set({
        metadata: { filename: randomFile, size: 0 },
        upload: { image: { mimetype: 'image/png', height: 0, width: 0, url }, pdf: { url: '' } }
      });
    } catch (error) {
      console.error('Failed to load random visual:', error);
    }
  }

  ngAfterViewInit(): void {
    let angle = 0;
    let lastTime = 0;
    const rotationSpeed = 1.1;
    const rotateGradient = (time: number): void => {
      if (lastTime === 0) lastTime = time;
      const deltaTime = (time - lastTime) / 10;
      lastTime = time;
      angle = (angle + rotationSpeed * deltaTime) % 360;
      this.gradientElement.nativeElement.style.setProperty("--gradient-angle", `${angle}deg`);
      requestAnimationFrame(rotateGradient);
    };
    requestAnimationFrame(rotateGradient);
  }

  onFileInputChange(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const f = input.files?.[0] ?? null;
    if (f) this.handleFile(f);
    if (input) input.value = '';
  }

  onDrop(ev: DragEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    const f = ev.dataTransfer?.files?.[0] ?? null;
    if (f) this.handleFile(f);
    this.isDragOver.set(false);
  }

  onDragOver(ev: DragEvent): void {
    ev.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave(ev: DragEvent): void {
    ev.preventDefault();
    this.isDragOver.set(false);
  }

  resetErrorMsg(): void {
    this.errorMsg.set(ErrorMessage.NONE);
  }

  /** Save the process once every page has finished its Callas check. */
  private saveProcess(): void {
    const pages = this.pages();
    if (!pages.length || this.simulationSaved()) return;
    if (!pages.every(p => p.skipped || !p.callasRunning)) return;
    this.simulationSaved.set(true);
    const errors = [...new Set(pages.flatMap(p => p.callasFiles?.errors ?? []))];
    this.simulationService.saveProcess(this.currentFileType(), this.getMockup(), null, errors)
      .catch(e => console.error('Failed to save process:', e));
  }

  private updatePage(idx: number, update: Partial<PageResult>): void {
    this.pages.update(pages => {
      const updated = [...pages];
      updated[idx] = { ...updated[idx], ...update };
      return updated;
    });
  }

  private async handleFile(f: File): Promise<void> {
    // Cancel any in-flight processing from a previous file
    this.abortController.abort();
    this.activeIntervals.forEach(clearInterval);
    this.activeIntervals = [];
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    this.isLoading.set(true);
    this.currentFileType.set(f.type);
    this.originalFileName.set(f.name);
    this.simulationSaved.set(false);
    this.orderNumber.reset();
    this.orderNumber.markAsUntouched();

    try {
      // Reject unsupported file types upfront
      const allowed = f.type === 'application/pdf' || f.type.startsWith('image/');
      if (!allowed) {
        this.errorMsg.set(ErrorMessage.UNSUPPORTED_FILE_TYPE);
        return;
      }

      // Step 1: slice PDF into individual pages, or wrap non-PDF as single entry
      let pageFiles: File[];
      if (f.type === 'application/pdf') {
        try {
          const blobs = await this.convertService.slicePdf(f);
          pageFiles = blobs.map((blob, i) =>
            new File([blob], `page_${i + 1}.pdf`, { type: 'application/pdf' })
          );
        } catch (e) {
          if ((e as Error)?.name === 'AbortError') return;
          pageFiles = [f]; // fallback: server slicing failed, treat as single page
        }
      } else {
        pageFiles = [f];
      }

      if (signal.aborted) return;

      if (pageFiles.length > 15) {
        this.errorMsg.set(ErrorMessage.TOO_MANY_PAGES);
        return;
      }

      this.pageFiles.set(pageFiles);
      this.totalPages.set(pageFiles.length);

      // Step 2: process page 0 to get dimensions for mockup detection + simulation display
      let firstResponse: ProcessResponse;
      try {
        firstResponse = await this.convertService.process(pageFiles[0], undefined, true, signal);
        this.fileTmp.set(pageFiles[0]);
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return;
        this.pages.set([]);
        this.totalPages.set(0);
        this.errorMsg.set(ErrorMessage.UNSUPPORTED_FILE_TYPE);
        return;
      }

      // Store reference dimensions — pages not matching these will be skipped
      this.refDimensions.set({
        width: firstResponse.upload.image.width,
        height: firstResponse.upload.image.height,
      });

      // Step 3: find matching mockup(s) and trigger full processing.
      // The atomic swap (pages.set + state.set) happens inside autoSelectMockupForFile /
      // selectMockupFromPicker so the old content stays visible until the mockup is confirmed.
      await this.autoSelectMockupForFile(firstResponse);
    } finally {
      this.isLoading.set(false);
    }
  }

  private async autoSelectMockupForFile(processResponse: ProcessResponse): Promise<void> {
    try {
      const { width, height } = processResponse.upload.image;
      if (!(width > 0 && height > 0)) return;
      const ratio = width / height;
      const matches = this.findMatchingMockupsByRatio(ratio);
      if (matches.length === 0) {
        this.pages.set([]);
        this.totalPages.set(0);
        this.errorMsg.set(ErrorMessage.NO_MATCHING_FORMAT);
        return;
      }
      this.errorMsg.set(ErrorMessage.NONE);
      if (matches.length === 1) {
        this.state.set(0);
        this.pages.set(this.pageFiles().map((_, i) => ({
          file: i === 0 ? processResponse : null,
          callasFiles: null, callasRunning: false, previewPdf: null, skipped: false
        })));
        this.setSelectedMockup(matches[0]);
        return;
      }
      this.pendingFile.set(processResponse);
      this.openPicker(matches);
    } catch {
      this.pages.set([]);
      this.totalPages.set(0);
      this.errorMsg.set(ErrorMessage.NO_MATCHING_FORMAT);
    }
  }

  private setSelectedMockup(mockupIdx: number): void {
    this.selectedMockupIdx.set(mockupIdx);
    const zooms = this.getZooms();
    const nextLastIdx = Math.max(0, zooms.length - 2);

    if (this.pageFiles().length === 0) {
      this.selectedZoomIdx.set(nextLastIdx);
      this.isNight.set(false);
      return;
    }

    const mockup = this.getMockup();

    // Process all pages in parallel; each updates independently as it completes.
    this.pageFiles().forEach(async (pageFile, idx) => {
      this.updatePage(idx, { callasRunning: true });
      try {
        const processResponse =
          await this.convertService.process(pageFile, mockup, true,
            this.abortController.signal);
        if (this.abortController.signal.aborted) return;

        // For pages 1+: validate dimensions and set display image.
        if (idx > 0) {
          const refDimensions = this.refDimensions();
          if (refDimensions) {
            const refW = Math.round(refDimensions.width);
            const refH = Math.round(refDimensions.height);
            const pageW = Math.round(processResponse.upload.image.width);
            const pageH = Math.round(processResponse.upload.image.height);
            if (pageW !== refW || pageH !== refH) {
              this.updatePage(idx, { callasRunning: false, skipped: true });
              return;
            }
          }
          this.updatePage(idx, { file: processResponse });
        }

        if (!processResponse.directoryId) {
          this.updatePage(idx, { callasRunning: false });
          this.saveProcess();
          return;
        }

        const directoryId = processResponse.directoryId;
        const checker = setInterval(async () => {
          if (this.abortController.signal.aborted) {
            clearInterval(checker);
            return;
          }

          const res = await this.convertService.check(directoryId);
          if (!res) return;
          clearInterval(checker);
          this.activeIntervals = this.activeIntervals.filter(i => i !== checker);

          const callasFile: PageResult['callasFiles'] = {
            bat: null, prod: null, prodIsZip: false, errors: null, checks: null, corrections: null
          };
          if (res.errorsUrl) {
            const report = await (await fetch(res.errorsUrl)).text();
            const [rules, fixups, errors] = report.split('_________') ?? ['', '', ''];
            callasFile!.checks = rules.split('\n').filter(l => l.trim());
            callasFile!.corrections = fixups.split('\n').filter(l => l.trim());
            callasFile!.errors = errors.split('\n').filter(l => l.trim());
          }
          if (res.batUrl) {
            callasFile!.bat = res.batUrl;
          }
          if (res.prodUrl) {
            callasFile!.prod = res.prodUrl;
            callasFile!.prodIsZip = res.prodUrl.split('?')[0].endsWith('.zip');
          }

          this.updatePage(idx, {
            callasRunning: false,
            callasFiles: callasFile,
            previewPdf: callasFile!.bat,
          });
          this.saveProcess();
        }, 5000);
        this.activeIntervals.push(checker);
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') {
          this.updatePage(idx, { callasRunning: false });
          return;
        }
        this.updatePage(idx, { callasRunning: false, skipped: true });
      }
    });

    this.selectedZoomIdx.set(nextLastIdx);
    this.isNight.set(false);
  }

  openPicker(indices: number[]): void {
    this.pickerMatches.set(indices);
    this.pickerVisible.set(true);
  }

  closePicker(): void {
    this.pickerVisible.set(false);
    this.pickerMatches.set([]);
    this.pendingFile.set(null);
  }

  selectMockupFromPicker(pickedMockupIdx: number): void {
    this.state.set(0);
    this.pages.set(this.pageFiles().map((_, i) => ({
      file: i === 0 ? this.pendingFile() : null,
      callasFiles: null, callasRunning: false, previewPdf: null, skipped: false
    })));
    this.pendingFile.set(null);
    this.setSelectedMockup(pickedMockupIdx);
    this.closePicker();
  }

  setState(state: number): void {
    if (state !== 0 && !this.callasFiles()) return;
    this.state.set(state);
  }

  pageStatusClass = (page: PageResult): string => {
    if (page.callasRunning) return 'bg-blue-400 animate-pulse';
    if (page.skipped) return 'bg-orange-400';
    if ((page.callasFiles?.errors?.length ?? 0) > 0) return 'bg-red-400';
    if (page.file !== null) return 'bg-green-400';
    return 'bg-white/30';
  };

  selectPage(idx: number): void {
    const page = this.pages()[idx];
    if (!page || page.skipped || page.file === null) return;
    this.selectedPageIdx.set(idx);
    this.pageDropdownOpen.set(false);
  }

  onDownloadClick(): void {
    if (!this.canDownload()) return;
    this.downloadScope.set(this.allDownloadable() ? 'all' : 'current');
    this.orderNumber.markAsUntouched();
    this.downloadPopupOpen.set(true);
  }

  async downloadCurrentPage(): Promise<void> {
    const prod = this.callasFiles()?.prod;
    if (!prod) return;
    this.downloadPopupOpen.set(false);
    const baseName = this.originalFileName().replace(/\.[^.]+$/, '') || 'production';
    const pageIdx = this.selectedPageIdx();
    const prodExt = this.callasFiles()!.prodIsZip ? '.zip' : '.pdf';
    const filename = this.pages().length > 1
      ? `${baseName}_page${pageIdx + 1}_production${prodExt}`
      : `${baseName}_production${prodExt}`;
    const blob = await fetch(prod).then(r => r.blob());
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  async downloadProduction(): Promise<void> {
    if (!this.canDownload()) return;
    this.downloadPopupOpen.set(false);
    const baseName = this.originalFileName().replace(/\.[^.]+$/, '') || 'production';
    const prodPages = this.pages()
      .map((p, i) => ({ page: p, idx: i }))
      .filter(({ page }) => !page.skipped && page.callasFiles?.prod != null);

    const link = document.createElement('a');

    if (prodPages.length === 1) {
      const singleProd = prodPages[0].page.callasFiles!.prod!;
      const blob = await fetch(singleProd).then(r => r.blob());
      link.href = URL.createObjectURL(blob);
      const ext = prodPages[0].page.callasFiles!.prodIsZip ? '.zip' : '.pdf';
      link.download = `${baseName}_production${ext}`;
      link.click();
      URL.revokeObjectURL(link.href);
      return;
    }

    const zipUrl = await this.convertService.zip(
      prodPages.map(({ page, idx }) => {
        const ext = page.callasFiles!.prodIsZip ? '.zip' : '.pdf';
        return { url: page.callasFiles!.prod!, filename: `${baseName}_page${idx + 1}_production${ext}` };
      })
    );
    link.href = zipUrl;
    link.download = `${baseName}_production.zip`;
    link.click();
  }

  async downloadWithOrder(): Promise<void> {
    if (!this.canDownload()) return;
    this.orderNumber.markAsTouched();
    if (this.orderNumber.invalid) return;
    const order = this.orderNumber.value.trim();
    const format = this.getMockup()?.format ?? 'format';
    const scope = this.downloadScope();
    this.downloadPopupOpen.set(false);

    const allPages = this.pages().map((p, i) => ({ page: p, idx: i }));
    const pagesToDownload = scope === 'current'
      ? allPages.filter(({ idx }) => idx === this.selectedPageIdx() && !allPages[idx].page.skipped)
      : allPages.filter(({ page }) =>
        !page.skipped && (page.callasFiles?.bat != null || page.callasFiles?.prod != null));

    const multiPage = pagesToDownload.length > 1;
    const files: { url: string; filename: string }[] = [];
    for (const { page, idx } of pagesToDownload) {
      const base = multiPage ? `${order}_${format}_page_${idx + 1}` : `${order}_${format}`;
      if (page.callasFiles?.bat) {
        files.push({ url: page.callasFiles.bat, filename: `fichiers_BAT/${base}.pdf` });
      }
      if (page.callasFiles?.prod) {
        const prodExt = page.callasFiles.prodIsZip ? '.zip' : '.pdf';
        files.push({ url: page.callasFiles.prod, filename: `fichiers_production/${base}${prodExt}` });
      }
    }

    if (files.length === 0) return;

    const zipUrl = await this.convertService.zip(files, `${order}.zip`);
    const link = document.createElement('a');
    link.href = zipUrl;
    link.click();
  }

  getMockup(): Mockup {
    const list = this.mockups();
    const selected = this.selectedMockupIdx();
    return list[Math.min(Math.max(0, selected), Math.max(0, list.length - 1))];
  }

  getZooms(): Zoom[] {
    const mk = this.getMockup();
    return mk?.zooms?.length ? mk.zooms : [{ name: 'Ajusté', scale: 1 }];
  }

  getImageSrc(): string {
    const isMobile = window.innerWidth <= 600;
    const mk = this.getMockup();
    const useNight = this.isNight() && !!mk?.img2;
    const filename = useNight ? mk?.img2 : mk?.img;
    return filename ? (isMobile ? `assets/mobile_mockups/${filename}` : `assets/mockups/${filename}`) : '';
  }

  currentScale(): number {
    const zooms = this.getZooms();
    return zooms[Math.min(Math.max(0, this.selectedZoomIdx()), Math.max(0, zooms.length - 1))]?.scale ?? 1;
  }

  selectDayNight(mode: 'day' | 'night'): void {
    this.isNight.set(mode === 'night');
  }

  selectZoom(idx: number): void {
    const zooms = this.getZooms();
    this.selectedZoomIdx.set(Math.min(Math.max(0, idx), Math.max(0, zooms.length - 1)));
  }

  hasVisual(): boolean {
    return !!this.getMockup()?.img;
  }

  hasNight(): boolean {
    return !!this.getMockup()?.img2;
  }

  getPenultimateZoom(mockup: Mockup): number {
    const length = mockup.zooms.length;
    return length >= 2 ? mockup.zooms[length - 2].scale : 1;
  }

  pageIndicatorClass(idx: number): string {
    const page = this.pages()[idx];
    if (!page) return 'bg-white/20';
    if (page.skipped) return 'bg-red-400';
    if (page.callasRunning) return 'bg-white animate-pulse';
    if (!page.callasRunning && page.file !== null) return 'bg-white';
    return 'bg-white/20';
  }

  getPageLabel(idx: number): string {
    return `Page ${idx + 1}`;
  }

  private parseRatio(r: string | null | undefined): number | null {
    if (!r) return null;
    const parts = r.split(':').map(s => Number(s.trim().replace(',', '.')));
    if (parts.length !== 2 || !isFinite(parts[0]) || !isFinite(parts[1]) || parts[0] <= 0 || parts[1] <= 0) return null;
    return parts[0] / parts[1];
  }

  private findMatchingMockupsByRatio(imageRatio: number): number[] {
    const list = this.mockups();
    const matches: number[] = [];
    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      const r = this.parseRatio(item?.ratio);

      if (r == null) {
        continue;
      }
      const diff = Math.abs(r - imageRatio);
      const tolerance = HomeComponent.RATIO_EPS * Math.max(1, r);
      const matched = diff <= tolerance;
      if (matched) {
        matches.push(i);
      }
    }
    return matches;
  }

  protected readonly document = document;
}
